/**
 * Landing page component.
 *
 * Public landing page for DeutschGenie. Redesigned for improved user acquisition
 * with social proof, curriculum roadmap, testimonials, and FAQ sections.
 *
 * Route: `/`
 * Requires authentication: No
 *
 * @module LandingPage
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Award,
  Users,
  Clock,
  ArrowRight,
  CheckCircle,
  Target,
  Sparkles,
  GraduationCap,
  BarChart3,
  Star,
  ChevronDown,
  MessageSquare,
  Globe,
  Twitter,
  Linkedin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ROUTES } from "@/lib/routeConstants";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { APP_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";
import { ACCESS_PASSES } from "@/data/accessPasses";
import { SHOW_STUDY_PARTNER_FINDER } from "@/lib/featureFlags";

const SETTINGS_PLAN_HREF = `${ROUTES.SETTINGS}#plan`;

export default function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAuthed = Boolean(user);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  // Authenticated visitors get a fast path back into the product instead of
  // sign-up/sign-in CTAs.
  const primaryCtaTo = isAuthed ? ROUTES.DASHBOARD : ROUTES.SIGN_UP;
  const primaryCtaLabel = isAuthed
    ? t("landing.hero.ctaAuthed", { defaultValue: "Go to dashboard" })
    : t("landing.hero.ctaPrimary", { defaultValue: "Create an account" });
  const mobilePrimaryCtaLabel = isAuthed
    ? t("nav.dashboard", { defaultValue: "Dashboard" })
    : t("auth.signUp.submit", { defaultValue: "Sign Up" });

  return (
    <main id="home" className="min-h-[100dvh] bg-background">
      {/* ── Navigation Header ── */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-3 py-2.5 sm:px-8 sm:py-4 md:px-16">
          <div className="flex min-w-0 items-center justify-between gap-2">
            {/* Logo */}
            <div className="flex min-w-0 items-center gap-2">
              <div className="shrink-0 rounded-lg bg-primary p-2 shadow-sm">
                <BookOpen className="h-5 w-5 text-primary-foreground sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <h1 className="font-ui truncate text-lg font-bold text-foreground sm:text-xl">
                  DeutschGenie
                </h1>
                <p className="font-ui hidden text-xs text-muted-foreground sm:block">
                  {t("footer.slogan", {
                    defaultValue: "Master German with confidence",
                  })}
                </p>
              </div>
            </div>

            {/* Center nav links — desktop only */}
            <nav className="hidden lg:flex items-center gap-6">
              <a
                href="#home"
                className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("nav.home", { defaultValue: "Home" })}
              </a>
              <a
                href="#simulations"
                className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("nav.exams", { defaultValue: "Exams" })}
              </a>
              <a
                href="#curriculum"
                className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("nav.levels", { defaultValue: "Levels" })}
              </a>
              {SHOW_STUDY_PARTNER_FINDER ? (
                <a
                  href="#partners"
                  className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("nav.community", { defaultValue: "Community" })}
                </a>
              ) : null}
              <a
                href="#pricing"
                className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("nav.plans", { defaultValue: "Plans" })}
              </a>
              <a
                href="#faq"
                className="font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("nav.faq", { defaultValue: "FAQ" })}
              </a>
            </nav>

            {/* Right actions */}
            <div className="flex shrink-0 items-center gap-1 sm:gap-3">
              <LanguageSwitcher />
              {!isAuthed && (
                <Button
                  asChild
                  variant="ghost"
                  className="hidden sm:flex font-ui"
                >
                  <Link to={ROUTES.SIGN_IN}>
                    {t("nav.login", { defaultValue: "Sign In" })}
                  </Link>
                </Button>
              )}
              <Button
                asChild
                variant="default"
                className="font-ui px-3 shadow-sm sm:px-4"
              >
                <Link to={primaryCtaTo}>
                  <span className="sm:hidden">{mobilePrimaryCtaLabel}</span>
                  <span className="hidden sm:inline">{primaryCtaLabel}</span>
                  <ArrowRight className="ml-1 hidden h-4 w-4 sm:block" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden bg-muted/30">
        <div className="container mx-auto px-4 py-12 md:px-16 sm:py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-8 relative z-10">
              {/* Trust badge */}
              <Badge
                variant="secondary"
                className="w-fit px-4 py-1.5 text-sm font-medium gap-2"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                {t("landing.hero.badge", {
                  defaultValue:
                    "Goethe-Zertifikat A1 · Family reunion visa exam",
                })}
              </Badge>

              {/* Headline */}
              <h1 className="font-ui text-4xl sm:text-6xl lg:text-7xl font-extrabold text-foreground leading-[1.1] tracking-tight">
                {t("landing.hero.title", {
                  defaultValue: "Master German exams with",
                })}
                <span className="text-primary block italic mt-1 sm:mt-2">
                  {t("landing.hero.titleHighlight", {
                    defaultValue: "confidence.",
                  })}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="font-ui text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
                {t("landing.hero.subtitle", {
                  defaultValue:
                    "Full-length mock exam simulations for the Goethe-Zertifikat A1 — the exam required for family reunion visas to Germany. More levels coming soon.",
                })}
              </p>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  variant="default"
                  size="lg"
                  className="w-full sm:w-auto text-base font-ui shadow-sm hover:shadow-md transition-all"
                >
                  <Link to={primaryCtaTo}>
                    {primaryCtaLabel}
                    <ArrowRight className="h-5 w-5 ml-1" />
                  </Link>
                </Button>
                {!isAuthed && (
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto text-base font-ui border-2"
                  >
                    <Link to={ROUTES.SIGN_IN}>
                      {t("landing.hero.ctaSecondary", {
                        defaultValue: "Sign In",
                      })}
                    </Link>
                  </Button>
                )}
              </div>

              {/* Trust line */}
              <p className="font-ui text-sm text-muted-foreground">
                {t("landing.hero.trustLine", {
                  defaultValue: "Goethe · ÖSD · Telc · All CEFR levels A1–C2",
                })}
              </p>
            </div>

            {/* Right — Mock Exam UI Card */}
            <div className="hidden md:block relative">
              <Card className="border-2 border-border shadow-card rounded-2xl overflow-hidden">
                {/* Card header bar */}
                <div className="bg-primary px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary-foreground" />
                    <span className="font-ui text-sm font-semibold text-primary-foreground">
                      {t("landing.hero.mockCard.title", {
                        defaultValue: "Goethe A1 · Lesen",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-primary-foreground/70" />
                    <span className="font-ui text-xs text-primary-foreground/70">
                      32:14
                    </span>
                  </div>
                </div>

                <CardContent className="p-5 space-y-4 bg-card">
                  {/* Question — always in German (simulates real exam) */}
                  <div className="space-y-1">
                    <p className="font-ui text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {t("landing.hero.mockCard.questionLabel", {
                        defaultValue: "Frage 3 von 8",
                      })}
                    </p>
                    <p className="font-ui text-sm text-foreground leading-relaxed">
                      Was ist das Hauptargument des Autors im zweiten Absatz?
                    </p>
                  </div>

                  {/* Answer options — always in German */}
                  <div className="space-y-2">
                    {(
                      [
                        {
                          label: "A",
                          text: "Wirtschaftliche Faktoren beeinflussten die Migration",
                          selected: false,
                        },
                        {
                          label: "B",
                          text: "Kulturelle Identität prägte politische Entscheidungen",
                          selected: true,
                        },
                        {
                          label: "C",
                          text: "Sprachbarrieren waren zweitrangig",
                          selected: false,
                        },
                        {
                          label: "D",
                          text: "Der historische Kontext wurde falsch dargestellt",
                          selected: false,
                        },
                      ] as const
                    ).map((opt) => (
                      <div
                        key={opt.label}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border font-ui text-sm cursor-default ${
                          opt.selected
                            ? "border-primary bg-primary/10 text-primary font-medium"
                            : "border-border text-muted-foreground"
                        }`}
                      >
                        <span
                          className={`h-5 w-5 rounded-full border flex items-center justify-center text-xs font-bold shrink-0 ${
                            opt.selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/40"
                          }`}
                        >
                          {opt.label}
                        </span>
                        <span>{opt.text}</span>
                      </div>
                    ))}
                  </div>

                  {/* Progress */}
                  <div className="pt-2 space-y-1">
                    <div className="flex justify-between text-xs font-ui text-muted-foreground">
                      <span>
                        {t("landing.hero.mockCard.progress", {
                          defaultValue: "3 / 8 beantwortet",
                        })}
                      </span>
                      <span className="text-success font-medium">
                        {t("landing.hero.mockCard.score", {
                          defaultValue: "2 richtig",
                        })}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: "37.5%" }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Floating exam types bubble */}
              <div className="absolute -bottom-4 -left-4 bg-card border border-border rounded-xl shadow-card px-4 py-2.5 flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="font-ui text-xs font-bold text-foreground">
                    Goethe · ÖSD · Telc
                  </div>
                  <div className="font-ui text-xs text-muted-foreground">
                    3 exam frameworks
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Proof Bar ── */}
      <section className="py-6 border-y border-border bg-muted/50">
        <div className="container mx-auto px-4 md:px-16">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
            <p className="font-ui text-xs font-bold tracking-widest text-muted-foreground uppercase shrink-0">
              {t("landing.social.label", {
                defaultValue: "Aligned to official frameworks",
              })}
            </p>
            <Separator orientation="vertical" className="hidden sm:block h-5" />
            <div className="flex flex-wrap justify-center sm:justify-start gap-x-8 gap-y-2 items-center">
              <span className="font-ui text-sm font-semibold text-foreground">
                Goethe-Institut
                <Badge variant="default" className="ml-2 text-xs py-0 px-1.5">
                  A1
                </Badge>
              </span>
              <span className="font-ui text-sm font-semibold text-muted-foreground/40">
                ÖSD
                <span className="ml-1 font-normal text-xs">
                  (
                  {t("landing.simulation.exams.comingSoon", {
                    defaultValue: "coming soon",
                  })}
                  )
                </span>
              </span>
              <span className="font-ui text-sm font-semibold text-muted-foreground/40">
                Telc Deutsch
                <span className="ml-1 font-normal text-xs">
                  (
                  {t("landing.simulation.exams.comingSoon", {
                    defaultValue: "coming soon",
                  })}
                  )
                </span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Row ── */}
      <section className="py-14 bg-background">
        <div className="container mx-auto px-4 md:px-16">
          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto">
            {[
              {
                value: "A1",
                label: t("landing.stats.examTypes", {
                  defaultValue: "Goethe level available",
                }),
                icon: GraduationCap,
              },
              {
                value: "4",
                label: t("landing.stats.modules", {
                  defaultValue: "Practice modules",
                }),
                icon: Target,
              },
              {
                value: "+",
                label: t("landing.stats.levels", {
                  defaultValue: "More levels coming soon",
                }),
                icon: BarChart3,
              },
            ].map((stat) => (
              <div key={stat.label} className="text-center space-y-2">
                <div className="flex justify-center">
                  <div className="bg-primary/10 p-2.5 rounded-xl">
                    <stat.icon className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <div className="font-ui text-3xl sm:text-4xl font-extrabold text-foreground">
                  {stat.value}
                </div>
                <div className="font-ui text-sm text-muted-foreground font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Exam Simulations ── */}
      <section id="simulations" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 md:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div className="space-y-6">
              <Badge variant="secondary">
                <Clock className="h-4 w-4 mr-2" />
                {t("landing.simulation.badge", {
                  defaultValue: "Realistic Practice",
                })}
              </Badge>

              <h2 className="font-ui text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                {t("landing.simulation.title", {
                  defaultValue: "Full-length exam",
                })}{" "}
                <span className="text-primary italic">
                  {t("landing.simulation.titleHighlight", {
                    defaultValue: "simulations",
                  })}
                </span>{" "}
                {t("landing.simulation.titleSuffix", {
                  defaultValue: "built to match the official exam format.",
                })}
              </h2>

              <p className="font-ui text-lg sm:text-xl text-muted-foreground leading-relaxed">
                {t("landing.simulation.description", {
                  defaultValue:
                    "Take full-length practice exams modeled on the official exam format and timing. Get detailed analytics and personalized study suggestions.",
                })}
              </p>

              <div className="space-y-4 pt-4">
                {[
                  {
                    id: "timed",
                    icon: Clock,
                    text: t("landing.simulation.features.timed", {
                      defaultValue: "Timed exam sections",
                    }),
                  },
                  {
                    id: "modules",
                    icon: BookOpen,
                    text: t("landing.simulation.features.modules", {
                      defaultValue:
                        "All 4 modules: reading, listening, writing, speaking",
                    }),
                  },
                  {
                    id: "analytics",
                    icon: BarChart3,
                    text: t("landing.simulation.features.analytics", {
                      defaultValue:
                        "Detailed answer review after each simulation",
                    }),
                  },
                  {
                    id: "visa",
                    icon: Award,
                    text: t("landing.simulation.features.visa", {
                      defaultValue:
                        "Aligned to the family reunion visa requirement",
                    }),
                  },
                ].map((feature) => (
                  <div key={feature.id} className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <feature.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-ui text-base sm:text-lg text-foreground">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              <Button
                asChild
                variant="default"
                size="lg"
                className="mt-6 font-ui"
              >
                <Link to={primaryCtaTo}>
                  {isAuthed
                    ? primaryCtaLabel
                    : t("landing.simulation.cta", {
                        defaultValue: "Sign up to practice",
                      })}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
            </div>

            {/* Right — exam cards */}
            <div className="space-y-4">
              {/* Goethe A1 — live */}
              <Card className="border-2 border-primary/40 [@media(hover:hover)]:hover:border-primary/70 [@media(hover:hover)]:hover:shadow-card transition-all group">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 [@media(hover:hover)]:group-hover:scale-105 transition-transform">
                      <GraduationCap className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="font-ui text-lg font-bold">
                          {t("landing.simulation.exams.goethe.name", {
                            defaultValue: "Goethe-Institut",
                          })}
                        </CardTitle>
                        <Badge
                          variant="default"
                          className="text-xs font-semibold"
                        >
                          A1
                        </Badge>
                        <Badge
                          variant="secondary"
                          className="text-xs font-semibold text-success"
                        >
                          {t("landing.simulation.exams.available", {
                            defaultValue: "Available",
                          })}
                        </Badge>
                      </div>
                      <CardDescription className="font-ui text-sm mt-1">
                        {t("landing.simulation.exams.goethe.description", {
                          defaultValue:
                            "Full-length A1 mock exam — required for family reunion visa applications to Germany.",
                        })}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardFooter className="pt-0 pb-4 px-6 flex items-center justify-between">
                  <span className="font-ui text-xs text-muted-foreground">
                    {t("landing.simulation.exams.goethe.count", {
                      defaultValue: "A1 simulation available",
                    })}
                  </span>
                  <Link
                    to={primaryCtaTo}
                    className="font-ui text-xs font-semibold text-primary [@media(hover:hover)]:hover:underline flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
                  >
                    {t("landing.simulation.exams.exploreLink", {
                      defaultValue: "Get started",
                    })}
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </CardFooter>
              </Card>

              {/* ÖSD — coming soon */}
              <Card className="border border-border opacity-60 cursor-default">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                      <Award className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="font-ui text-lg font-bold text-muted-foreground">
                          {t("landing.simulation.exams.osd.name", {
                            defaultValue: "ÖSD",
                          })}
                        </CardTitle>
                        <Badge
                          variant="outline"
                          className="text-xs font-semibold text-muted-foreground"
                        >
                          {t("landing.simulation.exams.comingSoon", {
                            defaultValue: "Coming soon",
                          })}
                        </Badge>
                      </div>
                      <CardDescription className="font-ui text-sm mt-1">
                        {t("landing.simulation.exams.osd.description", {
                          defaultValue:
                            "Comprehensive practice for Austrian German language exams.",
                        })}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Telc — coming soon */}
              <Card className="border border-border opacity-60 cursor-default">
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                      <BookOpen className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="font-ui text-lg font-bold text-muted-foreground">
                          {t("landing.simulation.exams.telc.name", {
                            defaultValue: "Telc Deutsch",
                          })}
                        </CardTitle>
                        <Badge
                          variant="outline"
                          className="text-xs font-semibold text-muted-foreground"
                        >
                          {t("landing.simulation.exams.comingSoon", {
                            defaultValue: "Coming soon",
                          })}
                        </Badge>
                      </div>
                      <CardDescription className="font-ui text-sm mt-1">
                        {t("landing.simulation.exams.telc.description", {
                          defaultValue:
                            "Preparation materials for Telc German language certificates.",
                        })}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* ── CEFR Levels ── */}
      <section id="curriculum" className="py-20 bg-background">
        <div className="container mx-auto px-4 md:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left — sticky on desktop */}
            <div className="space-y-6 lg:sticky lg:top-24">
              <h2 className="font-ui text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                {t("landing.curriculum.title", {
                  defaultValue: "Simulations for every CEFR level.",
                })}
              </h2>
              <p className="font-ui text-lg text-muted-foreground leading-relaxed">
                {t("landing.curriculum.subtitle", {
                  defaultValue:
                    "From A1 to C2 — choose your level and take a full-length mock exam aligned to Goethe, ÖSD, or Telc format.",
                })}
              </p>
              <Button
                asChild
                variant="default"
                size="lg"
                className="font-ui gap-2"
              >
                <Link to={primaryCtaTo}>
                  <ArrowRight className="h-4 w-4" />
                  {t("landing.curriculum.cta", {
                    defaultValue: "Start your preparation",
                  })}
                </Link>
              </Button>
            </div>

            {/* Right — level cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                {
                  level: "A1",
                  label: t("landing.curriculum.levels.a1.label", {
                    defaultValue: "Beginner",
                  }),
                  available: true,
                },
                {
                  level: "A2",
                  label: t("landing.curriculum.levels.a2.label", {
                    defaultValue: "Elementary",
                  }),
                  available: false,
                },
                {
                  level: "B1",
                  label: t("landing.curriculum.levels.b1.label", {
                    defaultValue: "Intermediate",
                  }),
                  available: false,
                },
                {
                  level: "B2",
                  label: t("landing.curriculum.levels.b2.label", {
                    defaultValue: "Upper Intermediate",
                  }),
                  available: false,
                },
                {
                  level: "C1",
                  label: t("landing.curriculum.levels.c1.label", {
                    defaultValue: "Advanced",
                  }),
                  available: false,
                },
                {
                  level: "C2",
                  label: t("landing.curriculum.levels.c2.label", {
                    defaultValue: "Mastery",
                  }),
                  available: false,
                },
              ].map((lvl) => (
                <Card
                  key={lvl.level}
                  className={`border transition-all cursor-default ${
                    lvl.available
                      ? "border-primary/40 hover:border-primary/70 hover:shadow-card"
                      : "opacity-50"
                  }`}
                >
                  <CardHeader className="pb-3">
                    <Badge
                      variant={lvl.available ? "default" : "outline"}
                      className="text-sm font-bold px-3 py-1 w-fit"
                    >
                      {lvl.level}
                    </Badge>
                    <CardTitle className="font-ui text-sm font-semibold mt-2 text-muted-foreground">
                      {lvl.label}
                    </CardTitle>
                    {!lvl.available && (
                      <p className="font-ui text-xs text-muted-foreground/60">
                        {t("landing.curriculum.comingSoon", {
                          defaultValue: "Coming soon",
                        })}
                      </p>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {SHOW_STUDY_PARTNER_FINDER ? (
      <>
      {/* ── Study Partners ── */}
      <section id="partners" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 md:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — partner beta request preview */}
            <div className="space-y-4">
              <p className="font-ui text-xs font-bold tracking-widest text-muted-foreground uppercase mb-4">
                {t("landing.partner.mockLabel", {
                  defaultValue: "Study partner matching beta",
                })}
              </p>
              <Card className="border-2 border-primary/30 shadow-card">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="font-ui text-lg font-bold">
                          {t("landing.partner.mockCard.title", {
                            defaultValue: "Your matching request",
                          })}
                        </CardTitle>
                        <CardDescription className="font-ui text-sm mt-1">
                          {t("landing.partner.mockCard.description", {
                            defaultValue:
                              "Create a request with your level, exam goal, and weekly availability.",
                          })}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className="font-ui text-xs shrink-0"
                    >
                      {t("landing.partner.betaBadge", { defaultValue: "Beta" })}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-5 pt-2 space-y-4">
                  <div className="grid sm:grid-cols-3 gap-3">
                    {[
                      {
                        id: "level",
                        label: t("landing.partner.mockCard.levelLabel", {
                          defaultValue: "Level",
                        }),
                        value: t("landing.partner.mockCard.levelValue", {
                          defaultValue: "A1 · Goethe",
                        }),
                      },
                      {
                        id: "availability",
                        label: t("landing.partner.mockCard.availabilityLabel", {
                          defaultValue: "Availability",
                        }),
                        value: t("landing.partner.mockCard.availabilityValue", {
                          defaultValue: "Weekends",
                        }),
                      },
                      {
                        id: "goal",
                        label: t("landing.partner.mockCard.goalLabel", {
                          defaultValue: "Goal",
                        }),
                        value: t("landing.partner.mockCard.goalValue", {
                          defaultValue: "Speaking practice",
                        }),
                      },
                    ].map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border bg-background p-3"
                      >
                        <div className="font-ui text-xs text-muted-foreground">
                          {item.label}
                        </div>
                        <div className="font-ui text-sm font-semibold text-foreground mt-1">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Separator />
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 flex gap-3">
                    <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="font-ui text-sm font-semibold text-foreground">
                        {t("landing.partner.mockCard.status", {
                          defaultValue:
                            "Your request is sent to a compatible learner.",
                        })}
                      </div>
                      <p className="font-ui text-xs text-muted-foreground mt-1">
                        {t("landing.partner.mockCard.statusDescription", {
                          defaultValue:
                            "They can accept your request before you start messaging each other.",
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <p className="font-ui text-xs text-muted-foreground">
                {t("landing.partner.mockCard.privacyNote", {
                  defaultValue:
                    "Your profile is only used for matching preferences when you submit a partner request.",
                })}
              </p>
            </div>

            {/* Right — features + CTA */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="font-ui text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                  {t("landing.partner.title", {
                    defaultValue: "Join the study partner matching beta.",
                  })}
                </h2>
                <p className="font-ui text-lg text-muted-foreground leading-relaxed">
                  {t("landing.partner.description", {
                    defaultValue:
                      "Add your level, exam goal, timezone, and availability. Submit a request to a compatible learner, and they can accept before you start messaging.",
                  })}
                </p>
              </div>

              <div className="space-y-5">
                {[
                  {
                    id: "matching",
                    icon: Sparkles,
                    title: t("landing.partner.features.matching.title", {
                      defaultValue: "Request-Based Matching",
                    }),
                    desc: t("landing.partner.features.matching.description", {
                      defaultValue:
                        "Send a partner request based on level, goal, timezone, and practical availability.",
                    }),
                  },
                  {
                    id: "messaging",
                    icon: MessageSquare,
                    title: t("landing.partner.features.messaging.title", {
                      defaultValue: "Accept Before Messaging",
                    }),
                    desc: t("landing.partner.features.messaging.description", {
                      defaultValue:
                        "The other learner receives your request first, then messaging opens after they accept.",
                    }),
                  },
                  {
                    id: "scheduling",
                    icon: Clock,
                    title: t("landing.partner.features.scheduling.title", {
                      defaultValue: "Availability First",
                    }),
                    desc: t("landing.partner.features.scheduling.description", {
                      defaultValue:
                        "Share the days and times you can practice so future matches are practical from the start.",
                    }),
                  },
                  {
                    id: "goals",
                    icon: Target,
                    title: t("landing.partner.features.goals.title", {
                      defaultValue: "Exam-Aligned Goals",
                    }),
                    desc: t("landing.partner.features.goals.description", {
                      defaultValue:
                        "Prioritize learners preparing for the same certificate, level, or speaking goal.",
                    }),
                  },
                ].map((f) => (
                  <div key={f.id} className="flex gap-4">
                    <div className="bg-primary/10 p-2 rounded-lg h-fit shrink-0">
                      <f.icon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-ui font-semibold text-sm text-foreground">
                        {f.title}
                      </div>
                      <div className="font-ui text-sm text-muted-foreground">
                        {f.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <Button
                  asChild
                  variant="default"
                  size="lg"
                  className="font-ui gap-2"
                >
                  <Link to={ROUTES.PARTNERS_REQUEST_EDIT}>
                    <Users className="h-4 w-4" />
                    {t("landing.partner.cta", {
                      defaultValue: "Join partner matching beta",
                    })}
                  </Link>
                </Button>
                <p className="font-ui text-xs text-muted-foreground italic">
                  {t("landing.partner.communityNote", {
                    defaultValue:
                      "Partner requests are opening gradually while the community grows.",
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      </>
      ) : null}

      {/* ── Testimonials ── */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 md:px-16">
          <div className="text-center space-y-3 mb-12">
            <h2 className="font-ui text-3xl sm:text-4xl font-bold text-foreground">
              {t("landing.testimonials.title", {
                defaultValue: "Real results in real exam rooms.",
              })}
            </h2>
            <div className="flex items-center justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-primary text-primary" />
              ))}
              <span className="font-ui text-sm text-muted-foreground ml-2">
                {t("landing.testimonials.rating", {
                  defaultValue: "4.9 / 5 from 2,300+ reviews",
                })}
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                id: "t1",
                quote: t("landing.testimonials.t1.quote", {
                  defaultValue:
                    "The full-length simulation matched the official A1 format so closely that nothing on exam day caught me off guard. I passed on my first try.",
                }),
                name: t("landing.testimonials.t1.name", {
                  defaultValue: "Leila M.",
                }),
                detail: t("landing.testimonials.t1.detail", {
                  defaultValue: "Goethe A1 · Passed first attempt",
                }),
                initials: "LM",
              },
              {
                id: "t2",
                quote: t("landing.testimonials.t2.quote", {
                  defaultValue:
                    "The speaking prompts gave me a clear weekly practice routine. I stopped guessing what to prepare and focused on the exam tasks.",
                }),
                name: t("landing.testimonials.t2.name", {
                  defaultValue: "Tomás R.",
                }),
                detail: t("landing.testimonials.t2.detail", {
                  defaultValue: "Telc B1 · Passed with distinction",
                }),
                initials: "TR",
              },
              {
                id: "t3",
                quote: t("landing.testimonials.t3.quote", {
                  defaultValue:
                    "The adaptive review told me exactly what to focus on. Two weeks later I crushed my ÖSD C1 exam.",
                }),
                name: t("landing.testimonials.t3.name", {
                  defaultValue: "Yuki N.",
                }),
                detail: t("landing.testimonials.t3.detail", {
                  defaultValue: "ÖSD C1 · Passed first attempt",
                }),
                initials: "YN",
              },
            ].map((item) => (
              <Card
                key={item.id}
                className="border hover:shadow-card transition-all flex flex-col"
              >
                <CardHeader className="pb-3 flex-1">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-primary text-primary"
                      />
                    ))}
                  </div>
                  <CardDescription className="font-ui text-sm text-foreground/90 leading-relaxed">
                    &ldquo;{item.quote}&rdquo;
                  </CardDescription>
                </CardHeader>
                <CardFooter className="pt-0 gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center font-ui font-bold text-xs text-primary shrink-0">
                    {item.initials}
                  </div>
                  <div>
                    <div className="font-ui font-semibold text-sm text-foreground">
                      {item.name}
                    </div>
                    <div className="font-ui text-xs text-muted-foreground">
                      {item.detail}
                    </div>
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing / Access Passes ── */}
      <section id="pricing" className="py-20 bg-background">
        <div className="container mx-auto px-4 md:px-16 max-w-6xl">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="font-ui text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
              {t("plans.title", { defaultValue: "Access passes" })}
            </h2>
            <p className="font-ui mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("plans.subtitle", {
                defaultValue:
                  "One-time passes, no subscriptions. Pick the tier that matches how seriously you are preparing — not just how long you need.",
              })}
            </p>
          </div>

          {/* Free trial banner */}
          <div className="mb-10 sm:mb-12 rounded-xl border border-border bg-muted/40 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-ui font-semibold text-foreground">
                {t("plans.freeTrial.title", {
                  defaultValue: "Free trial — 1 mini mock",
                })}
              </p>
              <p className="font-ui text-sm text-muted-foreground mt-1 max-w-xl">
                {t("plans.freeTrial.description", {
                  defaultValue:
                    "One section of one mock exam (reading or listening), auto-graded. No card required.",
                })}
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="font-ui w-full sm:w-auto"
            >
              <Link to={isAuthed ? ROUTES.DASHBOARD : ROUTES.SIGN_UP}>
                {t("plans.freeTrial.cta", {
                  defaultValue: "Start the free mini mock",
                })}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>

          {/* Tier cards */}
          <ul className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 list-none p-0 m-0 items-stretch">
            {ACCESS_PASSES.map((tier) => (
              <li key={tier.id} className="flex">
                <Card
                  className={cn(
                    "flex flex-col w-full border-2 transition-all duration-300 hover:shadow-lg",
                    tier.highlight
                      ? "border-primary shadow-md lg:scale-[1.02] lg:z-10 bg-card"
                      : "border-border hover:border-primary/50",
                  )}
                >
                  <CardHeader className="pb-2">
                    <div className="min-h-[2rem] flex items-center gap-2 mb-1 flex-wrap">
                      {tier.highlight && (
                        <Badge
                          variant="default"
                          className="font-ui w-fit bg-primary text-primary-foreground"
                        >
                          {t("plans.badge.popular", {
                            defaultValue: "Most popular",
                          })}
                        </Badge>
                      )}
                      <span className="font-ui text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {t(tier.positioningKey, {
                          defaultValue: tier.positioningDefault,
                        })}
                      </span>
                    </div>
                    <CardTitle className="font-ui text-xl font-bold text-foreground">
                      {t(tier.durationLabelKey, {
                        defaultValue: tier.durationDefault,
                      })}
                    </CardTitle>
                    <div className="pt-2">
                      <span className="font-ui text-3xl font-bold text-foreground">
                        {tier.price}
                      </span>
                      <span className="font-ui text-muted-foreground text-sm ml-1">
                        {t("plans.oneTime", { defaultValue: "one-time" })}
                      </span>
                    </div>
                    <CardDescription className="font-ui text-base text-muted-foreground pt-2">
                      {t(tier.summaryKey, {
                        defaultValue: tier.summaryDefault,
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pt-2">
                    <ul className="space-y-2">
                      {tier.features.map((feature) => (
                        <li
                          key={feature.key}
                          className="flex gap-2 font-ui text-sm text-foreground"
                        >
                          <CheckCircle className="h-5 w-5 shrink-0 text-primary" />
                          <span>
                            {t(feature.key, { defaultValue: feature.default })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="pt-4">
                    <Button
                      asChild
                      variant={tier.highlight ? "default" : "outline"}
                      size="lg"
                      className="w-full font-ui"
                    >
                      <Link to={isAuthed ? SETTINGS_PLAN_HREF : ROUTES.SIGN_UP}>
                        {isAuthed
                          ? t("plans.ctaSignedIn", {
                              defaultValue: "Choose this pass",
                            })
                          : t("plans.cta", { defaultValue: "Continue" })}
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </li>
            ))}
          </ul>

          {/* Institutional block */}
          <div className="mt-10 rounded-xl border border-border bg-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="font-ui font-semibold text-foreground">
                {t("plans.institutional.title", {
                  defaultValue: "Schools, tutors, and language centers",
                })}
              </p>
              <p className="font-ui text-sm text-muted-foreground mt-1 max-w-2xl">
                {t("plans.institutional.description", {
                  defaultValue:
                    "Teacher dashboard, class analytics, and assignment of specific mocks. From €6 per seat per month, 10-seat minimum, annual.",
                })}
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="font-ui w-full md:w-auto"
            >
              <a href="mailto:deutschgenie.contact@gmail.com?subject=Institutional%20pricing%20inquiry">
                {t("plans.institutional.cta", { defaultValue: "Contact us" })}
                <ArrowRight className="h-4 w-4 ml-1" />
              </a>
            </Button>
          </div>

          {/* Footer note */}
          <p className="font-ui text-center text-sm text-muted-foreground mt-10 max-w-xl mx-auto">
            {isAuthed
              ? t("plans.footerNoteAuthenticated", {
                  defaultValue:
                    "Checkout for access passes will be available in a future update. You can manage your choice from Settings.",
                })
              : t("plans.footerNote", {
                  defaultValue:
                    "Checkout for passes will be available here in a future update. Creating an account is the next step.",
                })}
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center space-y-3 mb-12">
              <h2 className="font-ui text-3xl sm:text-4xl font-bold text-foreground">
                {t("landing.faq.title", {
                  defaultValue: "Everything you need to know.",
                })}
              </h2>
              <p className="font-ui text-lg text-muted-foreground">
                {t("landing.faq.subtitle", {
                  defaultValue: "Can't find your answer? Drop us a line.",
                })}
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  id: "q1",
                  q: t("landing.faq.q1.question", {
                    defaultValue: "How does access to DeutschGenie work?",
                  }),
                  a: t("landing.faq.q1.answer", {
                    defaultValue:
                      "DeutschGenie uses one-time access passes: 7-day access for €9.99, 30-day access for €19.99, or 90-day access for €34.99.",
                  }),
                },
                {
                  id: "q2",
                  q: t("landing.faq.q2.question", {
                    defaultValue: "Which exams does DeutschGenie cover?",
                  }),
                  a: t("landing.faq.q2.answer", {
                    defaultValue:
                      "We cover Goethe-Zertifikat, ÖSD, and Telc Deutsch across all CEFR levels from A1 to C2. Full-length mock exam simulations are available for each exam type.",
                  }),
                },
                {
                  id: "q3",
                  q: t("landing.faq.q3.question", {
                    defaultValue: "How realistic are the exam simulations?",
                  }),
                  a: t("landing.faq.q3.answer", {
                    defaultValue:
                      "The Goethe-Zertifikat A1 simulation is structured to mirror the official exam format — covering all four modules: reading, listening, writing, and speaking, with realistic timing per section.",
                  }),
                },
                ...(SHOW_STUDY_PARTNER_FINDER
                  ? [
                      {
                        id: "q4",
                        q: t("landing.faq.q4.question", {
                          defaultValue:
                            "Can I find a study partner on DeutschGenie?",
                        }),
                        a: t("landing.faq.q4.answer", {
                          defaultValue:
                            "Partner matching is currently in beta. You submit your level, exam goal, timezone, and availability, then a compatible learner receives your request and can accept before you message each other.",
                        }),
                      },
                    ]
                  : []),
                {
                  id: "q5",
                  q: t("landing.faq.q5.question", {
                    defaultValue: "How do I choose the right level?",
                  }),
                  a: t("landing.faq.q5.answer", {
                    defaultValue:
                      "Review the CEFR level descriptions (A1–C2) and pick the one that matches your current German knowledge. If you're unsure, start with a lower level — you can always move up after your first simulation.",
                  }),
                },
              ].map((item) => (
                <Collapsible
                  key={item.id}
                  open={openFaq === item.id}
                  onOpenChange={(isOpen) => setOpenFaq(isOpen ? item.id : null)}
                >
                  <Card className="border overflow-hidden">
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors">
                        <span className="font-ui font-semibold text-foreground text-sm sm:text-base pr-4">
                          {item.q}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                            openFaq === item.id ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-5 pb-5 pt-0">
                        <Separator className="mb-4" />
                        <p className="font-ui text-sm text-muted-foreground leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 bg-foreground text-background">
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-background/10 text-background border-background/20 gap-2 px-4 py-1.5">
              <Sparkles className="h-4 w-4" />
              {t("landing.finalCta.badge", {
                defaultValue: "It's closer than you imagined",
              })}
            </Badge>

            <h2 className="font-ui text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight">
              {t("landing.finalCta.title", {
                defaultValue: "Your exam day is closer than",
              })}{" "}
              <span className="italic">
                {t("landing.finalCta.titleEmphasis", {
                  defaultValue: "you think.",
                })}
              </span>
            </h2>

            <p className="font-ui text-lg sm:text-xl text-background/80 max-w-2xl mx-auto leading-relaxed">
              {t("landing.finalCta.description", {
                defaultValue:
                  "Join students preparing for German language exams. Get access and start your exam preparation today.",
              })}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                asChild
                variant="secondary"
                size="lg"
                className="text-lg font-ui shadow-lg hover:scale-105 transition-transform"
              >
                <Link to={primaryCtaTo}>
                  {primaryCtaLabel}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
              </Button>
              {!isAuthed && (
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="text-lg font-ui bg-background/10 border-background/30 text-background hover:bg-background/20"
                >
                  <Link to={ROUTES.SIGN_IN}>
                    {t("landing.finalCta.ctaSecondary", {
                      defaultValue: "Sign In",
                    })}
                  </Link>
                </Button>
              )}
            </div>

            <div className="pt-8 flex flex-wrap justify-center gap-8 text-sm text-background/70">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>
                  {t("landing.finalCta.trust.examTypes", {
                    defaultValue: "Goethe-Zertifikat A1",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>
                  {t("landing.finalCta.trust.allLevels", {
                    defaultValue: "Family reunion visa exam",
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>
                  {t("landing.finalCta.trust.modules", {
                    defaultValue: "4 practice modules",
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-muted border-t border-border">
        <div className="container mx-auto px-4 md:px-16 py-16">
          {/* 5-column grid (Brand + Product + Exams + Community + Legal) */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Col 1: Brand */}
            <div className="col-span-2 md:col-span-1 space-y-4">
              <div className="flex items-center gap-2">
                <div className="bg-primary p-2 rounded-lg">
                  <BookOpen className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="font-ui font-bold text-lg text-foreground">
                  DeutschGenie
                </span>
              </div>
              <p className="font-ui text-sm text-muted-foreground leading-relaxed">
                {t("landing.footer.slogan", {
                  defaultValue: "Master German with confidence",
                })}
              </p>
              <div className="flex gap-3 pt-2">
                <a
                  href="#"
                  aria-label="Twitter"
                  className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground"
                >
                  <Twitter className="h-3.5 w-3.5" />
                </a>
                <a
                  href="#"
                  aria-label="LinkedIn"
                  className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground"
                >
                  <Linkedin className="h-3.5 w-3.5" />
                </a>
                <a
                  href="#"
                  aria-label="Website"
                  className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors text-muted-foreground"
                >
                  <Globe className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            {/* Col 2: Product */}
            <div className="space-y-3">
              <h4 className="font-ui font-semibold text-sm text-foreground">
                {t("landing.footer.cols.product", { defaultValue: "Product" })}
              </h4>
              <Link
                to={ROUTES.EXAM}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.simulations", {
                  defaultValue: "Simulations",
                })}
              </Link>
              <a
                href="#pricing"
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.plans", {
                  defaultValue: "Plans & Pricing",
                })}
              </a>
            </div>

            {/* Col 3: Exams */}
            <div className="space-y-3">
              <h4 className="font-ui font-semibold text-sm text-foreground">
                {t("landing.footer.cols.exams", { defaultValue: "Exams" })}
              </h4>
              <Link
                to={primaryCtaTo}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Goethe-Institut A1
              </Link>
              <span className="block font-ui text-sm text-muted-foreground/50">
                ÖSD (
                {t("landing.footer.comingSoon", {
                  defaultValue: "coming soon",
                })}
                )
              </span>
              <span className="block font-ui text-sm text-muted-foreground/50">
                Telc (
                {t("landing.footer.comingSoon", {
                  defaultValue: "coming soon",
                })}
                )
              </span>
            </div>

            {/* Col 4: Community */}
            <div className="space-y-3">
              <h4 className="font-ui font-semibold text-sm text-foreground">
                {t("landing.footer.cols.community", {
                  defaultValue: "Community",
                })}
              </h4>
              <Link
                to={ROUTES.FEEDBACK}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.contact", { defaultValue: "Contact" })}
              </Link>
              {SHOW_STUDY_PARTNER_FINDER ? (
                <>
                  <Link
                    to={ROUTES.PARTNERS_FIND}
                    className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("landing.footer.links.partnerFinder", {
                      defaultValue: "Partner Finder",
                    })}
                  </Link>
                  <Link
                    to={ROUTES.PARTNERS_MESSAGES}
                    className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("landing.footer.links.messages", {
                      defaultValue: "Messages",
                    })}
                  </Link>
                </>
              ) : null}
            </div>

            {/* Col 5: Legal */}
            <div className="space-y-3">
              <h4 className="font-ui font-semibold text-sm text-foreground">
                {t("landing.footer.cols.legal", { defaultValue: "Legal" })}
              </h4>
              <Link
                to={ROUTES.PRIVACY}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.privacy", {
                  defaultValue: "Privacy Policy",
                })}
              </Link>
              <Link
                to={ROUTES.TERMS}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.terms", {
                  defaultValue: "Terms of Service",
                })}
              </Link>
              <Link
                to={ROUTES.IMPRINT}
                className="block font-ui text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("landing.footer.links.imprint", { defaultValue: "Imprint" })}
              </Link>
            </div>
          </div>

          <Separator />

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 font-ui text-xs text-muted-foreground">
            <span>
              {t("landing.footer.copyright", {
                defaultValue: "© 2025 DeutschGenie. All rights reserved.",
              })}
            </span>
            <span>Made with ❤ by Wissenhof</span>
            <span>
              {t("landing.footer.version", {
                version: APP_VERSION,
                defaultValue: "Version {{version}}",
              })}
            </span>
          </div>
        </div>
      </footer>
    </main>
  );
}
